// Product rules are published "product-rule" documents in the "product-rules" folder of the global
// tree. Each rule targets products, variants and/or bundles, and can show a badge and restrict who
// may buy. The folder order is the priority. This file reads the shape (client-safe); fetching
// lives in product-rule-shape-helper.server.ts and what the rules mean for a variant or bundle in
// ProductRuleService (src/ui/services/product-rule-service.ts).
//
// - Targets: a product means all its variants; a variant means just that one.
// - Without target bundles the rule applies to the targets bought on their own. With target
//   bundles it applies to those bundles, and the targets then limit it to bundles in which one of
//   them is selected (no targets means every combination).
// - Badges, restrictions and "Show in feed" apply only where the rule applies: a rule for a
//   variant does not carry into the bundles containing it, those need their own rule.
// - A rule's badge is shown on product images and as an overview filter; it carries no logic.
// - "Show in feed" off hides what the rule applies to from the shopping feed; empty means on.

import { slugify } from '@/utils/url-helper';
import {
  PRODUCT_BADGE_COLORS,
  PRODUCT_BADGE_ICONS,
  PRODUCT_BADGE_POSITIONS,
  ProductBadgeModel,
} from '@/models/product-badge-model';

export const PRODUCT_RULES_FOLDER_NAME = 'product-rules';

const PRODUCT_RULE_SHAPE_IDENTIFIER = 'product-rule';

/** The "restricted-to" value for Nuki Club members. */
export const NUKI_CLUB_USER_GROUP = 'nuki-club';

export interface ProductRule {
  /** Products whose variants all match. */
  targetProductIds: string[];
  /** Single variants that match. */
  targetSkus: string[];
  /** When set, the rule applies to these bundles instead of the targets bought on their own. */
  targetBundleIds: string[];
  badge: ProductBadgeModel | null;
  /** User groups whose members may buy the targets; empty means everyone. */
  restrictedTo: string[];
  isShownInFeed: boolean;
}

/** A rule with the given fields; missing ones target nothing, show no badge, restrict nobody. */
export function buildProductRule(fields: Partial<ProductRule> = {}): ProductRule {
  return {
    targetProductIds: [],
    targetSkus: [],
    targetBundleIds: [],
    badge: null,
    restrictedTo: [],
    isShownInFeed: true,
    ...fields,
  };
}

interface ProductRuleRelationsContent {
  __typename?: string;
  items?: ReadonlyArray<{ id: string }> | null;
  productVariants?: ReadonlyArray<{ sku: string }> | null;
}

interface ProductRuleSelectionContent {
  __typename?: string;
  options?: ReadonlyArray<{ key: string; value: string }> | null;
}

/** A "product-rule" document as the product rules query returns it. */
export interface ProductRuleDocument {
  shape?: { identifier: string } | null;
  targets?: { content?: ProductRuleRelationsContent | null } | null;
  targetBundles?: { content?: ProductRuleRelationsContent | null } | null;
  badgeName?: { content?: { __typename?: string; text?: string | null } | null } | null;
  badgeType?: { content?: ProductRuleSelectionContent | null } | null;
  badgeColor?: { content?: ProductRuleSelectionContent | null } | null;
  badgeIcon?: { content?: ProductRuleSelectionContent | null } | null;
  badgePosition?: { content?: ProductRuleSelectionContent | null } | null;
  restrictedTo?: { content?: ProductRuleSelectionContent | null } | null;
  showInFeed?: { content?: { __typename?: string; value?: boolean | null } | null } | null;
}

// A selection option's value is its stable identifier; the key is the label editors see.
function getSelectedValues(content: ProductRuleSelectionContent | null | undefined): string[] {
  return (content?.options ?? []).map((option) => option.value);
}

function getAllowedValue<T extends string>(
  value: string | undefined,
  allowedValues: readonly T[],
): T | undefined {
  return allowedValues.find((allowedValue) => allowedValue === value);
}

/** The rule's badge, or null when it has no name. Unknown colours, icons and positions fall back. */
function parseProductBadge(document: ProductRuleDocument): ProductBadgeModel | null {
  const name = document.badgeName?.content?.text;

  if (!name) {
    return null;
  }

  const firstSelectedValue = (content: ProductRuleSelectionContent | null | undefined) =>
    getSelectedValues(content)[0];

  return {
    key: slugify(firstSelectedValue(document.badgeType?.content) ?? name),
    label: name,
    color: getAllowedValue(firstSelectedValue(document.badgeColor?.content), PRODUCT_BADGE_COLORS),
    icon: getAllowedValue(firstSelectedValue(document.badgeIcon?.content), PRODUCT_BADGE_ICONS),
    position:
      getAllowedValue(firstSelectedValue(document.badgePosition?.content), PRODUCT_BADGE_POSITIONS)
      ?? 'top-left',
  };
}

function parseProductRule(document: ProductRuleDocument): ProductRule {
  return {
    targetProductIds: (document.targets?.content?.items ?? []).map(({ id }) => id),
    targetSkus: (document.targets?.content?.productVariants ?? []).map(({ sku }) => sku),
    targetBundleIds: (document.targetBundles?.content?.items ?? []).map(({ id }) => id),
    badge: parseProductBadge(document),
    restrictedTo: getSelectedValues(document.restrictedTo?.content),
    isShownInFeed: document.showInFeed?.content?.value !== false,
  };
}

/** The rules of the "product-rules" folder's published documents, in folder order. */
export function parseProductRules(
  documents: ReadonlyArray<ProductRuleDocument> | null | undefined,
): ProductRule[] {
  return (documents ?? [])
    .filter((document) => document.shape?.identifier === PRODUCT_RULE_SHAPE_IDENTIFIER)
    .map(parseProductRule);
}

export function isRestrictedToNukiClub(restrictedTo: string[]): boolean {
  return restrictedTo.includes(NUKI_CLUB_USER_GROUP);
}
